import React, { useState } from 'react';
import { SearchBar } from '../components/SearchBar';
import { FilterChips } from '../components/FilterChips';
import { AdvancedFilters } from '../components/AdvancedFilters';
import { SearchResults } from '../components/SearchResults';
import { StatsPanel } from '../components/StatsPanel';
import { ApiService } from '../services/api.service';
import type { SearchFilters, SearchResponse } from '../types/api.types';

type Page = 'home' | 'search' | 'chatbot';

interface SearchPageProps {
  onNavigate?: (page: Page) => void;
}

export const SearchPage: React.FC<SearchPageProps> = ({ onNavigate }) => {
  const [query, setQuery] = useState('');
  const [topK, setTopK] = useState(5);
  const [filters, setFilters] = useState<SearchFilters>({});
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const [searchResponse, setSearchResponse] = useState<SearchResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTime, setSearchTime] = useState<number>(0);

  const handleSearch = async (searchQuery?: string) => {
    const queryToUse = searchQuery !== undefined ? searchQuery : query;
    setQuery(queryToUse);
    setError(null);
    setIsLoading(true);

    const startTime = performance.now();

    try {
      const response = await ApiService.search({
        query: queryToUse,
        top_k: topK,
        filters: Object.keys(filters).length > 0 ? filters : undefined,
      });

      const endTime = performance.now();
      setSearchTime((endTime - startTime) / 1000);
      setSearchResponse(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al realizar la búsqueda');
      console.error('Search error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setShowAdvancedFilters(false);
    handleSearch();
  };

  const stats = searchResponse?.results.reduce(
    (acc, result) => {
      if (result.score !== null) {
        acc.scores.push(result.score);
        acc.bestScore = acc.bestScore === null ? result.score : Math.min(acc.bestScore, result.score);
      }
      return acc;
    },
    { scores: [] as number[], bestScore: null as number | null }
  );

  const avgScore = stats?.scores.length ? stats.scores.reduce((a, b) => a + b, 0) / stats.scores.length : null;

  return (
    <div className="flex-1 bg-white flex flex-col">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white px-4 sm:px-6 py-4 shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button
            onClick={() => onNavigate?.('home')}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Inicio
          </button>
          <div className="w-px h-5 bg-gray-200" />
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#0943B5] rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <div className="min-w-0">
              <h1 className="text-sm font-semibold text-gray-900 truncate">Búsqueda en Biblioteca</h1>
              <p className="text-xs text-gray-400 hidden sm:block">Búsqueda semántica · Biblioteca PQR</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 w-full px-4 sm:px-6 py-6 sm:py-8">
        {/* Search section */}
        <div className="mb-6 sm:mb-8">
          <div className="mb-4 sm:mb-6">
            <SearchBar onSearch={handleSearch} isLoading={isLoading} />
          </div>

          <div className="flex items-center gap-4 mb-4">
            <FilterChips
              topK={topK}
              onTopKChange={(value) => {
                setTopK(value);
                if (searchResponse) handleSearch();
              }}
              onShowAdvancedFilters={() => setShowAdvancedFilters(!showAdvancedFilters)}
            />
          </div>

          {showAdvancedFilters && (
            <div className="mb-6">
              <AdvancedFilters
                filters={filters}
                onFiltersChange={setFilters}
                onClose={() => setShowAdvancedFilters(false)}
                onApply={handleApplyFilters}
              />
            </div>
          )}
        </div>

        {/* Error state */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">❌ {error}</p>
          </div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0943B5]"></div>
          </div>
        )}

        {/* Results section */}
        {!isLoading && searchResponse && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3">
              <SearchResults
                results={searchResponse.results}
                total={searchResponse.total}
                query={searchResponse.query}
                searchTime={searchTime}
              />
            </div>
            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-24">
                <StatsPanel
                  total={searchResponse.total}
                  searchTime={searchTime}
                  bestScore={stats?.bestScore || null}
                  avgScore={avgScore}
                />
              </div>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !searchResponse && !error && (
          <div className="text-center py-12">
            <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-cobalt-light flex items-center justify-center">
              <svg className="w-12 h-12 text-cobalt" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 16 16">
                <path d="M1.5 4 Q1.5 3 2 3 H5 Q6 3 6.5 5 H11 Q12 5 12 6 V9.5"/>
                <path d="M1.5 4 V13 Q1.5 14 2 14 H8.5"/>
                <line x1="1.5" y1="7" x2="12" y2="7"/>
                <circle cx="11" cy="12" r="2.5"/>
                <line x1="12.8" y1="13.8" x2="14.5" y2="15.5"/>
              </svg>
            </div>
            <p className="text-gray-600 text-lg">Realiza una búsqueda para comenzar</p>
            <p className="text-gray-400 text-sm mt-2">
              Busca documentos PQR usando lenguaje natural o aplica filtros específicos para encontrar información relevante.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};
